import { useState } from "react";
import { useNavigate, useLocation, useMatch } from "react-router-dom";
import { entities } from "byzantine";
import {
  useBreakpoints,
  LoadingSkeleton,
  LoadingShim,
} from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import {
  useFetchPaginatedPaymentActivity,
  useFormattedPaginatedPaymentActivity,
  usePaymentActivityTotal,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import type { PaymentCancelAction } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import { useDeleteScheduledACHPayment } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import {
  useCancelInternationalWire,
  useCancelScheduledInternationalWire,
} from "byzantine/src/dbbl/businessLogic/modules/internationalWires";
import { useCancelDualApproval } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals";
import { useLocalization } from "@fluent/react";
import PageNavigation from "../../page_navigation/PageNavigation";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import FilterContainer from "../Filters/FilterContainer";
import { FilterChips, FilterChipsType } from "../Filters/filterConfig";
import { SearchBar, EmptyState, CancelPaymentDialog } from "../common";
import { SudoProvider } from "../../SudoContext";
import { PaymentActivityTable } from "./";
import ReverseFlow from "./ReverseFlow";
import usePaymentActivityContainer from "../hooks";
import {
  PAYMENT_ACTIVITY_ROUTE,
  getReverseRoute,
  getReverseFromListRoute,
} from "../routes";

type CancelTarget = PaymentCancelAction;

const IndividualPaymentContainer = () => {
  const features = useUserFeatures();
  const isBusinessUser = entities.useIsBusinessUser();
  const { m, l } = useBreakpoints();
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const [cancelTarget, setCancelTarget] = useState<CancelTarget | null>(null);
  // reverse can be launched two ways:
  // - from the drawer (:activityUuid/reverse) → keep the drawer open behind the modal
  // - from the table (reverse/:reverseActivityUuid) → modal only, no drawer
  const reverseDrawerMatch = useMatch(`${getReverseRoute(":activityUuid")}/*`);
  const reverseListMatch = useMatch(
    `${getReverseFromListRoute(":reverseActivityUuid")}/*`
  );
  const reverseUuid =
    reverseDrawerMatch?.params.activityUuid ??
    reverseListMatch?.params.reverseActivityUuid ??
    null;
  const keepDrawerOpen = Boolean(reverseDrawerMatch);
  const { deleteScheduledACHPayment } = useDeleteScheduledACHPayment();
  const { cancelDualApproval } = useCancelDualApproval();
  const { cancelInternationalWire } = useCancelInternationalWire();
  const { cancelScheduledInternationalWire } =
    useCancelScheduledInternationalWire();
  const {
    expedited_payments: expeditedPaymentsFeature,
    ach_payments_collect: achPaymentsCollectFeature,
    wires: wiresFeature,
    international_wires: internationalWiresFeature,
    international_wires_consumer: internationalWiresConsumerFeature,
  } = features;

  const {
    state,
    formattedPaginatedPayments,
    paymentsLoading,
    bootstrapLoading,
    pagination,
    handleSearchChange,
    handleSetFilters,
    handleReset,
    refetchCurrentPage,
  } = usePaymentActivityContainer({
    useFetchPaginated: useFetchPaginatedPaymentActivity,
    useFormattedPaginated: useFormattedPaginatedPaymentActivity,
    useTotal: usePaymentActivityTotal,
    useBootstrap: entities.paymentActivity.bootstrap.useBootstrap,
  });

  const toggleCancelDialog = (target?: CancelTarget) => {
    setCancelTarget(target ?? null);
  };

  const handleCancelPayment = () => {
    if (!cancelTarget) return;
    const { kind, model } = cancelTarget;

    const closeDrawerAndRefetch = async () => {
      toggleCancelDialog();
      navigate(`/${PAYMENT_ACTIVITY_ROUTE}${location.search}`);
      await refetchCurrentPage({ disableCaching: true });
    };

    if (kind === "dualApproval") {
      // wires and ACH reversals are cancelled by cancelling their dual approval
      cancelDualApproval(cancelTarget.id as API.DualApprovalId, model, {
        onSuccess: closeDrawerAndRefetch,
      });
    } else if (kind === "consumerSwiftWire") {
      // consumer international wires are cancelled directly during their hold window
      cancelInternationalWire(cancelTarget.id as API.SwiftTransactionId, {
        onSuccess: closeDrawerAndRefetch,
      });
    } else if (kind === "scheduledSwiftWire") {
      cancelScheduledInternationalWire(
        cancelTarget.id as API.SwiftTransactionId,
        {
          onSuccess: closeDrawerAndRefetch,
        }
      );
    } else {
      deleteScheduledACHPayment(cancelTarget.id as API.ScheduledACHPaymentId, {
        onSuccess: closeDrawerAndRefetch,
      });
    }
  };

  const renderActivityTable = () => {
    // while the reverse/sudo flow is active, keep the table mounted
    if (paymentsLoading && !reverseUuid) return <LoadingShim isLoading />;
    if (formattedPaginatedPayments.length === 0 && !reverseUuid) {
      return <EmptyState state={state} handleReset={handleReset} />;
    }

    return (
      <div className={cc({ "margin--top--l": !(m || l) })}>
        <PaymentActivityTable
          payments={
            formattedPaginatedPayments as API.FormattedPaymentActivity[]
          }
          handleToggleCancelDialog={toggleCancelDialog}
        />
        <PageNavigation
          pagination={pagination}
          marginClasses="margin--all--l"
        />
      </div>
    );
  };

  const ALLOWED_FILTERS = [
    FilterChips.DATE,
    FilterChips.STATUS,
    FilterChips.AMOUNT,
    FilterChips.ACCOUNT,
    FilterChips.FREQUENCY,
  ] as FilterChipsType[];

  const PARENT_SELECTABLE_FILTERS = [
    FilterChips.STATUS,
    FilterChips.AMOUNT,
    FilterChips.ACCOUNT,
    FilterChips.FREQUENCY,
  ] as FilterChipsType[];

  if (features.invoice_pay) {
    ALLOWED_FILTERS.push(FilterChips.ATTACHMENTS);
    PARENT_SELECTABLE_FILTERS.push(FilterChips.ATTACHMENTS);
  }

  if (
    expeditedPaymentsFeature ||
    wiresFeature ||
    internationalWiresFeature ||
    internationalWiresConsumerFeature
  ) {
    ALLOWED_FILTERS.push(FilterChips.METHOD);
  }

  if (achPaymentsCollectFeature) {
    ALLOWED_FILTERS.push(FilterChips.ACTION);
    PARENT_SELECTABLE_FILTERS.push(FilterChips.ACTION);
  }

  const hiddenFilterStatuses = [
    ...(isBusinessUser ? ["on_hold"] : []),
    ...(!isBusinessUser || !features.dual_approval ? ["pending_approval"] : []),
  ];

  return (
    <LoadingSkeleton isLoading={bootstrapLoading}>
      <div className="margin--top--xl">
        <SearchBar
          handleSearchChange={handleSearchChange}
          searchValue={state.searchValue}
          placeholder={l10n.getString("payment-activity-search-label")}
        />
      </div>
      <div>
        <FilterContainer
          setFilters={handleSetFilters}
          filters={state.filters}
          allowedFilters={ALLOWED_FILTERS}
          parentSelectableFilters={PARENT_SELECTABLE_FILTERS}
          hideFilterStatus={
            hiddenFilterStatuses.length ? hiddenFilterStatuses : undefined
          }
        />
      </div>
      {renderActivityTable()}
      {cancelTarget && (
        <CancelPaymentDialog
          isOpen={Boolean(cancelTarget)}
          handleClose={() => toggleCancelDialog()}
          handleCancel={handleCancelPayment}
        />
      )}
      {reverseUuid && (
        <SudoProvider
          basename={
            keepDrawerOpen
              ? getReverseRoute(reverseUuid)
              : getReverseFromListRoute(reverseUuid)
          }
        >
          <ReverseFlow
            activityUuid={reverseUuid}
            keepDrawerOpen={keepDrawerOpen}
            onComplete={async () => {
              await (
                refetchCurrentPage({ disableCaching: true }) as Promise<unknown>
              ).catch(() => undefined);
              navigate(`/${PAYMENT_ACTIVITY_ROUTE}${location.search}`);
            }}
          />
        </SudoProvider>
      )}
    </LoadingSkeleton>
  );
};

export default IndividualPaymentContainer;
